import {
    collection,
    doc,
    getDocs,
    addDoc,
    updateDoc,
    deleteDoc,
    query,
    orderBy,
    serverTimestamp,
    getDoc
} from 'firebase/firestore';
import { db } from './config';

export interface Category {
    id?: string;
    name: string;
    description?: string;
    color?: string;
    order: number;
    isActive: boolean;
    createdAt?: any;
    updatedAt?: any;
}

const COLLECTION_NAME = 'projectCategories';

// Get all categories
export const getAllCategories = async (): Promise<Category[]> => {
    try {
        // Use simple query that doesn't require composite index
        const q = query(
            collection(db, COLLECTION_NAME),
            orderBy('order', 'asc')
        );
        const querySnapshot = await getDocs(q);

        const categories = querySnapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
        })) as Category[];

        // Sort by name in JavaScript as fallback
        return categories.sort((a, b) => {
            if (a.order === b.order) {
                return a.name.localeCompare(b.name);
            }
            return a.order - b.order;
        });
    } catch (error) {
        console.error('Error fetching categories:', error);

        // Fallback: try without any ordering if order field doesn't exist
        try {
            console.log('Trying fallback query without ordering...');
            const fallbackSnapshot = await getDocs(collection(db, COLLECTION_NAME));
            const categories = fallbackSnapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data()
            })) as Category[];

            return categories.sort((a, b) => {
                const orderA = a.order || 0;
                const orderB = b.order || 0;
                if (orderA === orderB) {
                    return a.name.localeCompare(b.name);
                }
                return orderA - orderB;
            });
        } catch (fallbackError) {
            console.error('Fallback query also failed:', fallbackError);
            throw new Error('Failed to fetch categories');
        }
    }
};

// Get active categories only
export const getActiveCategories = async (): Promise<Category[]> => {
    try {
        const allCategories = await getAllCategories();
        return allCategories.filter(category => category.isActive);
    } catch (error) {
        console.error('Error fetching active categories:', error);
        throw new Error('Failed to fetch active categories');
    }
};

// Get category by ID
export const getCategoryById = async (id: string): Promise<Category | null> => {
    try {
        const docRef = doc(db, COLLECTION_NAME, id);
        const docSnap = await getDoc(docRef);

        if (docSnap.exists()) {
            return {
                id: docSnap.id,
                ...docSnap.data()
            } as Category;
        }
        return null;
    } catch (error) {
        console.error('Error fetching category:', error);
        throw new Error('Failed to fetch category');
    }
};

// Create new category
export const createCategory = async (categoryData: Omit<Category, 'id' | 'createdAt' | 'updatedAt'>): Promise<string> => {
    try {
        const docRef = await addDoc(collection(db, COLLECTION_NAME), {
            ...categoryData,
            createdAt: serverTimestamp(),
            updatedAt: serverTimestamp()
        });
        return docRef.id;
    } catch (error) {
        console.error('Error creating category:', error);
        throw new Error('Failed to create category');
    }
};

// Update category
export const updateCategory = async (id: string, categoryData: Partial<Omit<Category, 'id' | 'createdAt'>>): Promise<void> => {
    try {
        const docRef = doc(db, COLLECTION_NAME, id);
        await updateDoc(docRef, {
            ...categoryData,
            updatedAt: serverTimestamp()
        });
    } catch (error) {
        console.error('Error updating category:', error);
        throw new Error('Failed to update category');
    }
};

// Delete category
export const deleteCategory = async (id: string): Promise<void> => {
    try {
        const docRef = doc(db, COLLECTION_NAME, id);
        await deleteDoc(docRef);
    } catch (error) {
        console.error('Error deleting category:', error);
        throw new Error('Failed to delete category');
    }
};

// Reorder categories
export const reorderCategories = async (categories: { id: string; order: number }[]): Promise<void> => {
    try {
        const updatePromises = categories.map(({ id, order }) => {
            const docRef = doc(db, COLLECTION_NAME, id);
            return updateDoc(docRef, {
                order,
                updatedAt: serverTimestamp()
            });
        });

        await Promise.all(updatePromises);
    } catch (error) {
        console.error('Error reordering categories:', error);
        throw new Error('Failed to reorder categories');
    }
};

// Seed default categories (run once to initialize)
export const seedDefaultCategories = async (): Promise<void> => {
  try {
    console.log('Starting to seed default categories...');
    
    const existingCategories = await getAllCategories();
    console.log('Existing categories count:', existingCategories.length);
    
    if (existingCategories.length > 0) {
      console.log('Categories already exist:', existingCategories.map(c => c.name));
      console.log('Skipping seed - delete existing categories first if you want to re-seed');
      return;
    }

    console.log('No existing categories found, proceeding with seeding...');

        const defaultCategories: Omit<Category, 'id' | 'createdAt' | 'updatedAt'>[] = [
            {
                name: 'Web Application',
                description: 'Full-stack web applications and websites',
                color: '#3B82F6',
                order: 1,
                isActive: true
            },
            {
                name: 'Mobile Application',
                description: 'iOS and Android mobile applications',
                color: '#10B981',
                order: 2,
                isActive: true
            },
            {
                name: 'Desktop Application',
                description: 'Desktop software and applications',
                color: '#8B5CF6',
                order: 3,
                isActive: true
            },
            {
                name: 'Backend / API',
                description: 'Backend services, APIs and microservices',
                color: '#F59E0B',
                order: 4,
                isActive: true
            },
            {
                name: 'Other',
                description: 'Other types of projects and experiments',
                color: '#6B7280',
                order: 5,
                isActive: true
            }
        ];

        console.log('Creating default categories...');

        // Create categories one by one for better error tracking
        for (const category of defaultCategories) {
            try {
                console.log('Creating category:', category.name);
                await createCategory(category);
                console.log('Successfully created:', category.name);
            } catch (err) {
                console.error('Failed to create category:', category.name, err);
                throw err;
            }
        }

        console.log('All default categories seeded successfully');
    } catch (error) {
        console.error('Error seeding default categories:', error);
        // Provide more specific error details
        if (error instanceof Error) {
            throw new Error(`Failed to seed default categories: ${error.message}`);
        } else {
            throw new Error('Failed to seed default categories: Unknown error occurred');
        }
    }
}; 